import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  deleteDoc,
  setDoc,
} from "firebase/firestore";

import { v4 as uuidv4 } from "uuid";

import { httpsCallable } from "firebase/functions";

import { firestoreDb, functions } from "../firebase";
import { getEmptySchool } from "../models/school";
import { updateSubscriptionAdminUsers } from "./subscriptionFunctions";

export async function getSchoolUnassignedCodeCount(schoolID) {
  try {
    const q = query(
      collection(firestoreDb, `schools/${schoolID}/accessIDs`),
      where("Assigned", "==", false)
    );
    let snapshot = await getDocs(q);
    return snapshot.docs.length;
  } catch (e) {
    console.log(e);
    return 0;
  }
}

export async function cleanSchoolAccessCodes(schoolID) {
  try {
    console.log(`Starting cleanup for school: ${schoolID}`);

    const q = query(collection(firestoreDb, `schools/${schoolID}/accessIDs`));
    let snapshot = await getDocs(q);
    console.log(`Total accessIDs found: ${snapshot.docs.length}`);

    const accountIdMap = new Map();
    const duplicateGuids = [];

    await Promise.all(snapshot.docs.map(async (d) => {
      const subscription = d.data();
      const subscriptionRef = d.ref;

      console.log(`Checking accessID: ${subscription.Guid}, AccountID: ${subscription.AccountID}`);

      if (subscription.AccountID && subscription.AccountID !== "") {
        // Track occurrences of each AccountID
        if (accountIdMap.has(subscription.AccountID)) {
          duplicateGuids.push(subscription.Guid); // Mark duplicate for deletion
          console.log(`Duplicate found: ${subscription.AccountID} -> Guid: ${subscription.Guid}`);
        } else {
          accountIdMap.set(subscription.AccountID, subscription.Guid);
        }

        // Check if the AccountID exists in the users collection
        const userRef = doc(firestoreDb, `schools/${schoolID}/users/${subscription.AccountID}`);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
          console.log(`Orphaned AccountID detected: ${subscription.AccountID}, fixing subscription...`);
          await fixSubscription(schoolID, subscription.Guid);
        } else {
          console.log(`AccountID exists: ${subscription.AccountID}, no action needed.`);
        }
      } else {
        console.log(`Skipping: No AccountID associated with Guid: ${subscription.Guid}`);
      }
    }));

    // Handle duplicates: delete excess accessIDs with the same AccountID
    await Promise.all(duplicateGuids.map(async (guid) => {
      await fixSubscription(schoolID, guid);
      console.log(`Deleted duplicate accessID with Guid: ${guid}`);
    }));

    console.log(`Cleanup complete. Processed ${snapshot.docs.length} accessIDs.`);
    return snapshot.docs.length;
  } catch (e) {
    console.error("Error cleaning access codes:", e);
    return 0;
  }
}


export async function createAccessIDs(schoolID, n) {
  try {
    console.log(`Creating ${n} new accessIDs for school: ${schoolID}`);

    const accessIDsCollection = collection(firestoreDb, `schools/${schoolID}/accessIDs`);
    const createdAccessIDs = [];

    for (let i = 0; i < n; i++) {
      const accessID = uuidv4(); // Generate a unique GUID
      const accessRef = doc(accessIDsCollection, accessID);

      // AccessID structure
      const accessData = {
        Guid: accessID,
        AccountID: "", // Empty by default, can be assigned later
        Assigned: false
      };

      await setDoc(accessRef, accessData);
      createdAccessIDs.push(accessID);
      console.log(`Created accessID: ${accessID}`);
    }

    console.log(`Successfully created ${n} new accessIDs.`);
    return createdAccessIDs;
  } catch (e) {
    console.error("Error creating accessIDs:", e);
    return [];
  }
}


export async function updateSchoolDetails(schoolID, details) {
  try {
    const q = query(doc(firestoreDb, `schools/${schoolID}`));
    let res = await updateDoc(q, details);
    return res;
  } catch (e) {
    console.log(e);
    return {};
  }
}

export async function getSchoolDetails(schoolID) {
  try {
    const q = query(doc(firestoreDb, `schools/${schoolID}`));
    let snapshot = await getDoc(q);
    let details = snapshot.data();
    return details;
  } catch (e) {
    console.log(e);
    return {};
  }
}

export async function getSchoolUsers(schoolID) {
  try {
    const q = query(collection(firestoreDb, `schools/${schoolID}/users`));
    let snapshot = await getDocs(q);
    let docs = snapshot.docs.map((doc) => doc.data());

    return docs;
  } catch (e) {
    console.log(e);
    return [];
  }
}

export async function getSchoolAccessIDs(schoolID) {
  try {
    const q = query(collection(firestoreDb, `schools/${schoolID}/accessIDs`));
    let snapshot = await getDocs(q);
    let docs = snapshot.docs.map((doc) => doc.data());

    return docs;
  } catch (e) {
    console.log(e);
    return [];
  }
}

export async function checkIfUserSubscribed(userID, schoolID) {
  try {
    if (!userID || !schoolID) {
      return false;
    }
    // const q = query(
    //   collection(firestoreDb, `schools/${schoolID}/accessIDs`),
    //   where("AccountID", "in", [userID])
    // );

    const q = query(doc(firestoreDb, `schools/${schoolID}/users/${userID}`));
    // let snapshot = await getDocs(q);
    // return snapshot.docs.length >= 1;

    let snapshot = await getDoc(q);
    return snapshot.data().HasAccess;
  } catch (e) {
    console.log(e);
    return false;
  }
}

export async function checkIfTeacherHasSchool(userID, schoolID) {
  try {
    if (!userID || !schoolID) {
      return false;
    }
    const q = query(doc(firestoreDb, `schools/${schoolID}/users/${userID}`));
    let snapshot = await getDoc(q);
    return snapshot.exists();
  } catch (e) {
    console.log(e);
    return false;
  }
}

export async function addTeacherToSchool(teacherEmail, schoolID) {
  try {
    const addTeacherToSchoolFunction = httpsCallable(
      functions,
      "addTeacherToSchool"
    );
    let response = await addTeacherToSchoolFunction({
      TeacherEmail: teacherEmail,
      SchoolID: schoolID,
    });
    return response.data.Status === "Success";
  } catch (e) {
    console.log(e);
    return false;
  }
}

export async function removeTeacherFromSchool(schoolID, teacherID) {
  try {
    let subscriptionRemoved = await removeSubscriptionFromTeacher(
      schoolID,
      teacherID
    );
    if (!subscriptionRemoved) {
      return false;
    }
    const schoolRef = query(
      doc(firestoreDb, `schools/${schoolID}/users/${teacherID}`)
    );
    let res = await deleteDoc(schoolRef);

    // const userRef = query(doc(firestoreDb, `users/${teacherID}`));
    // let res2 = await updateDoc(userRef, { SchoolID: "" });
    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
}

export async function removeSubscriptionFromTeacher(schoolID, teacherID) {
  try {
    const q = query(
      collection(firestoreDb, `schools/${schoolID}/accessIDs`),
      where("AccountID", "in", [teacherID])
    );
    let snapshot = await getDocs(q);
    if (snapshot.docs.length === 0) {
      return true;
    }
    let id = snapshot.docs[0].data().Guid;

    const accessID = query(
      doc(firestoreDb, `schools/${schoolID}/accessIDs/${id}`)
    );
    let res2 = await updateDoc(accessID, { AccountID: "", Assigned: false });

    const userRef = query(
      doc(firestoreDb, `schools/${schoolID}/users/${teacherID}`)
    );
    let res3 = await updateDoc(userRef, { HasAccess: false });
    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
}

export async function unassignAllSubscriptions(schoolID) {
  try {
    const addTeacherToSchoolFunction = httpsCallable(
      functions,
      "unassignSubscriptions"
    );
    let response = await addTeacherToSchoolFunction({
      SchoolID: schoolID,
    });
    return response.data.Status === "Success";
  } catch (e) {
    console.log(e);
    return false;
  }
}

export async function fixSubscription(schoolID, subscriptionID) {
  try {
    const q = query(
      doc(firestoreDb, `schools/${schoolID}/accessIDs/${subscriptionID}`)
    );
    await updateDoc(q, {
      AccountID: "",
      Assigned: false,
    });
    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
}

export async function assignSubscriptionToTeacher(schoolID, teacherID) {
  try {
    const q = query(
      collection(firestoreDb, `schools/${schoolID}/accessIDs`),
      where("Assigned", "in", [false])
    );
    let snapshot = await getDocs(q);

    if (snapshot.docs.length === 0) {
      return false;
    }

    let id = snapshot.docs[0].data().Guid;

    const accessID = query(
      doc(firestoreDb, `schools/${schoolID}/accessIDs/${id}`)
    );
    let res2 = await updateDoc(accessID, {
      AccountID: teacherID,
      Assigned: true,
    });

    const userRef = query(
      doc(firestoreDb, `schools/${schoolID}/users/${teacherID}`)
    );
    let res3 = await updateDoc(userRef, { HasAccess: true });
    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
}

export async function assignExactSubscriptionToTeacher(
  schoolID,
  teacherID,
  subscriptionID
) {
  try {
    const accessID = query(
      doc(firestoreDb, `schools/${schoolID}/accessIDs/${subscriptionID}`)
    );
    let res2 = await updateDoc(accessID, {
      AccountID: teacherID,
      Assigned: true,
    });

    const userRef = query(
      doc(firestoreDb, `schools/${schoolID}/users/${teacherID}`)
    );
    let res3 = await updateDoc(userRef, { HasAccess: true });
    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
}

export async function updateSchoolAdminUsers(
  schoolID,
  adminUsers,
  subscriptionRef
) {
  try {
    const q = query(doc(firestoreDb, `schools/${schoolID}`));
    let res = await updateDoc(q, { AdminID: adminUsers });
    let res2 = await updateSubscriptionAdminUsers(subscriptionRef, adminUsers);
    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
}

export async function createInitialSchool(initialData) {
  let school = getEmptySchool();
  let schoolID = uuidv4().toString();
  school = {
    ...school,
    ...initialData,
    Guid: schoolID,
  };

  try {
    const q = query(doc(firestoreDb, `schools/${schoolID}`));
    let res = await setDoc(q, school);
    return schoolID;
  } catch (e) {
    console.log(e);
    return null;
  }
}

export async function assignAllSubscriptions(schoolID) {
  try {
    const assignSubscriptionsFunction = httpsCallable(
      functions,
      "assignSubscriptions"
    );
    let response = await assignSubscriptionsFunction({
      SchoolID: schoolID,
    });
    return response.data.Status === "Success";
  } catch (e) {
    console.log(e);
    return false;
  }
}
